import { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { useMemeInteraction, type ResizeHandle } from '../../hooks/useMemeInteraction';
import { useCanvasViewport } from '../../hooks/useCanvasViewport';
import { useMemeStore } from '../../stores';
import { drawWrappedText } from '../../utils/canvasText';
import { isBackgroundLayer, isCharacterLayer, isFrameLayer } from '../../utils/layerUtils';

// Mobile responsive constants
const MAX_VIEWPORT_WIDTH_DESKTOP = 600;
const MAX_VIEWPORT_HEIGHT_DESKTOP = 600;
const MAX_VIEWPORT_WIDTH_MOBILE = 320;
const MAX_VIEWPORT_HEIGHT_MOBILE = 400;
const MOBILE_BREAKPOINT = 640; // Tailwind sm breakpoint
const MOBILE_KEYBOARD_THRESHOLD = 80;
const MOBILE_KEYBOARD_REDUCTION = 60;
const MIN_MOBILE_VIEWPORT_HEIGHT = 300;

interface MemeCanvasProps {
  maxViewportWidth?: number;
  maxViewportHeight?: number;
  onCharacterTap?: () => void;
}

export function MemeCanvas({
  onCharacterTap,
  maxViewportWidth: propMaxWidth,
  maxViewportHeight: propMaxHeight,
}: MemeCanvasProps) {
  const getResponsiveViewport = useCallback(() => {
    if (typeof window === 'undefined') {
      return {
        width: propMaxWidth ?? MAX_VIEWPORT_WIDTH_DESKTOP,
        height: propMaxHeight ?? MAX_VIEWPORT_HEIGHT_DESKTOP,
      };
    }

    const isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    if (!isMobile) {
      return {
        width: propMaxWidth ?? MAX_VIEWPORT_WIDTH_DESKTOP,
        height: propMaxHeight ?? MAX_VIEWPORT_HEIGHT_DESKTOP,
      };
    }

    let mobileHeight = MAX_VIEWPORT_HEIGHT_MOBILE;
    const viewport = window.visualViewport;
    if (viewport) {
      const keyboardInset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop);
      if (keyboardInset > MOBILE_KEYBOARD_THRESHOLD) {
        mobileHeight = Math.max(
          MIN_MOBILE_VIEWPORT_HEIGHT,
          MAX_VIEWPORT_HEIGHT_MOBILE - MOBILE_KEYBOARD_REDUCTION
        );
      }
    }

    return {
      width: MAX_VIEWPORT_WIDTH_MOBILE,
      height: mobileHeight,
    };
  }, [propMaxWidth, propMaxHeight]);

  // Responsive viewport size based on screen width
  const initialViewport = getResponsiveViewport();
  const [maxViewportWidth, setMaxViewportWidth] = useState(initialViewport.width);
  const [maxViewportHeight, setMaxViewportHeight] = useState(initialViewport.height);

  // Update viewport size on window resize
  useEffect(() => {
    const handleResize = () => {
      const next = getResponsiveViewport();
      setMaxViewportWidth(next.width);
      setMaxViewportHeight(next.height);
    };
    const viewport = window.visualViewport;
    handleResize();
    window.addEventListener('resize', handleResize);
    viewport?.addEventListener('resize', handleResize);
    viewport?.addEventListener('scroll', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      viewport?.removeEventListener('resize', handleResize);
      viewport?.removeEventListener('scroll', handleResize);
    };
  }, [getResponsiveViewport]);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [containerAvailableWidth, setContainerAvailableWidth] = useState<number | null>(null);
  const [loadedImages, setLoadedImages] = useState<Map<string, HTMLImageElement>>(new Map());
  const [loadedLayers, setLoadedLayers] = useState<
    Map<string, Map<string, HTMLImageElement>>
  >(new Map());
  const [backgroundImg, setBackgroundImg] = useState<HTMLImageElement | null>(null);

  // Measure available container width to prevent canvas from being clipped
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setContainerAvailableWidth(entry.contentRect.width);
      }
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, []);

  const {
    canvasWidth,
    canvasHeight,
    backgroundImage,
    backgroundImageScale,
    backgroundImageOffset,
    backgroundColor,
    nftElements,
    textElements,
    selectedElementId,
    selectedElementType,
    selectElement,
    updateNFTElementPosition,
    updateNFTElementScale,
    updateNFTElementRotation,

    bringToFront,
    sendToBack,
    updateTextElement,
    setDragging,
    setResizing,
    setBackgroundImageOffset,
  } = useMemeStore();

  const aspectRatio = canvasWidth / canvasHeight;
  // Clamp viewport to available container width (minus padding for the p-2 container)
  const containerPadding = 16; // p-2 = 8px each side
  const effectiveMaxWidth = containerAvailableWidth
    ? Math.min(maxViewportWidth, containerAvailableWidth - containerPadding)
    : maxViewportWidth;
  let viewportWidth = effectiveMaxWidth;
  let viewportHeight = effectiveMaxWidth / aspectRatio;

  if (viewportHeight > maxViewportHeight) {
    viewportHeight = maxViewportHeight;
    viewportWidth = maxViewportHeight * aspectRatio;
  }

  const handleUpdateTextPosition = useCallback(
    (elementId: string, position: { x: number; y: number }) => {
      updateTextElement(elementId, { position });
    },
    [updateTextElement]
  );

  const handleUpdateTextRotation = useCallback(
    (elementId: string, rotation: number) => {
      updateTextElement(elementId, { rotation });
    },
    [updateTextElement]
  );

  const handleUpdateTextFontSize = useCallback(
    (elementId: string, fontSize: number) => {
      updateTextElement(elementId, { fontSize });
    },
    [updateTextElement]
  );

  const { isDragging, isResizing, isRotating, hoveredHandle, snapGuides, getNFTElementBounds, getTextElementBounds } = useMemeInteraction({
    canvasRef,
    nftElements,
    textElements,
    selectedElementId,
    selectedElementType,
    canvasWidth,
    canvasHeight,
    onSelectElement: selectElement,
    onUpdateNFTPosition: updateNFTElementPosition,
    onUpdateTextPosition: handleUpdateTextPosition,
    onUpdateNFTScale: updateNFTElementScale,
    onUpdateTextFontSize: handleUpdateTextFontSize,
    onUpdateNFTRotation: updateNFTElementRotation,
    onUpdateTextRotation: handleUpdateTextRotation,
    onBringToFront: (elementId, elementType) => bringToFront(elementId, elementType),
    onSendToBack: (elementId, elementType) => sendToBack(elementId, elementType),
    onDragStart: () => setDragging(true),
    onDragEnd: () => setDragging(false),
    onResizeStart: (handle) => setResizing(true, handle),
    onResizeEnd: () => setResizing(false, null),
    onCharacterTap,
    loadedImages,
  });

  // Check if any NFT element has sticker mode enabled
  const isStickerModeActive = nftElements.some((el) => el.stickerMode);

  // Viewport controls for background image zoom/pan
  const { isPanning } = useCanvasViewport({
    containerRef,
    canvasRef,
    canvasWidth,
    canvasHeight,
    backgroundImageScale,
    backgroundImageOffset,
    onScaleChange: (scale) => {
      const { setBackgroundImageScale } = useMemeStore.getState();
      setBackgroundImageScale(scale);
    },
    onOffsetChange: setBackgroundImageOffset,
    hasBackgroundImage: !!backgroundImage,
    isElementSelected: !!selectedElementId,
    isDraggingElement: isDragging,
    isStickerModeActive,
  });

  // Persistent URL-keyed image cache. Survives re-renders and reload cycles so a
  // layer URI fetched once is never fetched again while this canvas is mounted.
  const imageCacheRef = useRef(new Map<string, HTMLImageElement>());

  const loadImage = useCallback((url: string): Promise<HTMLImageElement> => {
    const cached = imageCacheRef.current.get(url);
    if (cached) return Promise.resolve(cached);
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        imageCacheRef.current.set(url, img);
        resolve(img);
      };
      img.onerror = () => reject(new Error(`Failed to load image: ${url}`));
      img.src = url;
    });
  }, []);

  useEffect(() => {
    if (!backgroundImage) {
      setBackgroundImg(null);
      return;
    }

    loadImage(backgroundImage)
      .then(setBackgroundImg)
      .catch(() => setBackgroundImg(null));
  }, [backgroundImage, loadImage]);

  // Stable identity key for the NFT/layer sources on the canvas. Position, scale,
  // rotation and zIndex changes (every drag frame) intentionally do NOT change this
  // key, so dragging never triggers a reload cycle.
  const nftSourceKey = useMemo(
    () =>
      nftElements
        .map(
          (e) =>
            `${e.id}:${e.nftData.mintAddress}:${e.nftData.imageUrl}:${e.nftData.layerAssets
              .map((l) => `${l.id}=${l.uri}`)
              .join(',')}`
        )
        .join('|'),
    [nftElements]
  );

  useEffect(() => {
    // Guard against out-of-order resolution: a slower earlier run must never
    // overwrite the maps of a later run (previously dropped freshly loaded layers).
    let cancelled = false;

    const loadAllImages = async () => {
      const newImages = new Map<string, HTMLImageElement>();
      const newLayers = new Map<string, Map<string, HTMLImageElement>>();

      for (const element of nftElements) {
        if (cancelled) return;
        const mintAddress = element.nftData.mintAddress;

        if (!newImages.has(mintAddress)) {
          try {
            const img = await loadImage(element.nftData.imageUrl);
            if (cancelled) return;
            newImages.set(mintAddress, img);
          } catch (err) {
            console.error('Failed to load NFT image:', err);
          }
        }

        if (element.nftData.layerAssets.length > 0) {
          const layerImages = new Map<string, HTMLImageElement>();
          for (const layer of element.nftData.layerAssets) {
            if (cancelled) return;
            try {
              const layerImg = await loadImage(layer.uri);
              if (cancelled) return;
              layerImages.set(layer.id, layerImg);
            } catch (err) {
              console.error('Failed to load layer:', layer.id, err);
            }
          }
          newLayers.set(element.id, layerImages);
        }
      }

      if (!cancelled) {
        setLoadedImages(newImages);
        setLoadedLayers(newLayers);
      }
    };

    loadAllImages();

    return () => {
      cancelled = true;
    };
    // Keyed on nftSourceKey (not the element objects) so transform-only updates
    // skip this effect entirely. nftElements is read from the closure of the
    // render that produced the current key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nftSourceKey, loadImage]);



  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Disable antialiasing for crisp pixel art rendering
    ctx.imageSmoothingEnabled = false;

    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Draw background color first
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);

    // Draw uploaded background image on top (scaled, centered, and with offset for pan)
    // Skip if sticker mode is active (we only want the character on solid background)
    if (backgroundImg && !isStickerModeActive) {
      const scaledWidth = backgroundImg.width * backgroundImageScale;
      const scaledHeight = backgroundImg.height * backgroundImageScale;
      const drawX = (canvasWidth - scaledWidth) / 2 + backgroundImageOffset.x;
      const drawY = (canvasHeight - scaledHeight) / 2 + backgroundImageOffset.y;

      ctx.drawImage(backgroundImg, drawX, drawY, scaledWidth, scaledHeight);
    }

    // Sort NFT elements by zIndex for proper layering
    const sortedNFTElements = [...nftElements].sort((a, b) => a.zIndex - b.zIndex);

    // Find if any element has sticker mode enabled
    const stickerModeElement = nftElements.find((el) => el.stickerMode);

    // Combine and sort all elements by zIndex for character/text rendering
    // If sticker mode is active, only include the sticker mode element (no text)
    const allElements: Array<{ type: 'nft' | 'text'; element: typeof nftElements[0] | typeof textElements[0] }> = stickerModeElement
      ? [{ type: 'nft' as const, element: stickerModeElement }]
      : [
          ...nftElements.map((e) => ({ type: 'nft' as const, element: e })),
          ...textElements.map((e) => ({ type: 'text' as const, element: e })),
        ];
    if (!stickerModeElement) {
      allElements.sort((a, b) => a.element.zIndex - b.element.zIndex);
    }

    // Helper function to draw background layer to fill canvas
    const drawBackgroundLayer = (layerImg: HTMLImageElement) => {
      const imgAspect = layerImg.width / layerImg.height;
      const canvasAspect = canvasWidth / canvasHeight;

      let drawWidth: number;
      let drawHeight: number;
      let drawX: number;
      let drawY: number;

      if (imgAspect > canvasAspect) {
        drawHeight = canvasHeight;
        drawWidth = drawHeight * imgAspect;
        drawX = (canvasWidth - drawWidth) / 2;
        drawY = 0;
      } else {
        drawWidth = canvasWidth;
        drawHeight = drawWidth / imgAspect;
        drawX = 0;
        drawY = (canvasHeight - drawHeight) / 2;
      }

      ctx.drawImage(layerImg, drawX, drawY, drawWidth, drawHeight);
    };

    const shouldRenderNFTBackgrounds = !backgroundImg;

    // PASS 1: Draw ALL background layers first (in z-order)
    // Skip if sticker mode is active or a custom background image is present
    if (!stickerModeElement && shouldRenderNFTBackgrounds) {
      for (const nftEl of sortedNFTElements) {
        const elementLayers = loadedLayers.get(nftEl.id);
        if (!elementLayers || elementLayers.size === 0) continue;
        if (!nftEl.useBackgroundLayer) continue;

        const backgroundLayers = nftEl.nftData.layerAssets.filter(isBackgroundLayer);

        for (const layer of backgroundLayers) {
          const layerImg = elementLayers.get(layer.id);
          if (layerImg) {
            drawBackgroundLayer(layerImg);
          }
        }
      }
    }

    // PASS 2: Draw ALL character layers and text elements (in z-order)
    for (const { type, element } of allElements) {
      ctx.save();

      if (type === 'nft') {
        const nftEl = element as typeof nftElements[0];
        const elementLayers = loadedLayers.get(nftEl.id);
        const hasLayers = elementLayers && elementLayers.size > 0;

        ctx.translate(nftEl.position.x, nftEl.position.y);
        ctx.rotate((nftEl.rotation * Math.PI) / 180);

        if (hasLayers && elementLayers) {
          const characterLayers = nftEl.nftData.layerAssets.filter((layer) => {
            if (nftEl.selectedLayers.length > 0) {
              return nftEl.selectedLayers.includes(layer.id);
            }

            // Background/frame handled in pass 1 / pass 3
            return isCharacterLayer(layer);
          });

          // Get reference dimensions from composite NFT image for consistent layer scaling
          const refImage = loadedImages.get(nftEl.nftData.mintAddress);
          const refWidth = refImage ? refImage.width : 512;
          const refHeight = refImage ? refImage.height : 512;

          for (const layer of characterLayers) {
            const layerImg = elementLayers.get(layer.id);
            if (layerImg) {
              const scaledWidth = refWidth * nftEl.scale;
              const scaledHeight = refHeight * nftEl.scale;
              ctx.drawImage(
                layerImg,
                -scaledWidth / 2,
                -scaledHeight / 2,
                scaledWidth,
                scaledHeight
              );
            }
          }
        } else {
          // Fallback: draw full NFT image at position and scale
          const image = loadedImages.get(nftEl.nftData.mintAddress);
          if (image) {
            const scaledWidth = image.width * nftEl.scale;
            const scaledHeight = image.height * nftEl.scale;
            ctx.drawImage(image, -scaledWidth / 2, -scaledHeight / 2, scaledWidth, scaledHeight);
          }
        }
      } else {
        const textEl = element as typeof textElements[0];
        ctx.translate(textEl.position.x, textEl.position.y);
        ctx.rotate((textEl.rotation * Math.PI) / 180);

        ctx.direction = 'ltr';
        ctx.font = `${textEl.fontSize}px ${textEl.fontFamily}`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';

        drawWrappedText(ctx, textEl.text, textEl.fontSize, canvasWidth * 0.9, {
          color: textEl.color,
          strokeColor: textEl.strokeColor,
          strokeWidth: textEl.strokeWidth,
        });
      }

      ctx.restore();
    }

    // PASS 3: Draw ALL frame layers last (in z-order)
    // Skip entirely if any element has sticker mode enabled
    if (!stickerModeElement) {
      for (const nftEl of sortedNFTElements) {
        const elementLayers = loadedLayers.get(nftEl.id);
        if (!elementLayers || elementLayers.size === 0) continue;
        if (!nftEl.useFrameLayer) continue;

        const frameLayers = nftEl.nftData.layerAssets.filter(isFrameLayer);

        for (const layer of frameLayers) {
          const layerImg = elementLayers.get(layer.id);
          if (layerImg) {
            drawBackgroundLayer(layerImg);
          }
        }
      }
    }

    // Draw selection for NFT elements with resize handles
    if (selectedElementType === 'nft') {
      const selectedElement = nftElements.find((e) => e.id === selectedElementId);
      if (selectedElement) {
        const bounds = getNFTElementBounds(selectedElement);
        if (bounds) {
          ctx.save();

          // Draw selection border
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 5]);
          ctx.strokeRect(bounds.left, bounds.top, bounds.width, bounds.height);

          // Draw resize handles
          ctx.setLineDash([]);
          ctx.fillStyle = '#ffffff';
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 2;

          const handleSize = 8;
          const halfHandle = handleSize / 2;

          // Helper to draw a handle
          const drawHandle = (x: number, y: number) => {
            ctx.fillRect(x - halfHandle, y - halfHandle, handleSize, handleSize);
            ctx.strokeRect(x - halfHandle, y - halfHandle, handleSize, handleSize);
          };

          // Corner handles
          drawHandle(bounds.left, bounds.top); // NW
          drawHandle(bounds.right, bounds.top); // NE
          drawHandle(bounds.right, bounds.bottom); // SE
          drawHandle(bounds.left, bounds.bottom); // SW

          // Edge handles (middle of each side)
          drawHandle((bounds.left + bounds.right) / 2, bounds.top); // N
          drawHandle((bounds.left + bounds.right) / 2, bounds.bottom); // S
          drawHandle(bounds.left, (bounds.top + bounds.bottom) / 2); // W
          drawHandle(bounds.right, (bounds.top + bounds.bottom) / 2); // E

          ctx.restore();
        }
      }
    }

    // Draw selection for text elements with resize handles
    if (selectedElementType === 'text') {
      const selectedElement = textElements.find((e) => e.id === selectedElementId);
      if (selectedElement) {
        const textBounds = getTextElementBounds(selectedElement);
        if (textBounds) {
          ctx.save();

          const left = textBounds.left - 5;
          const top = textBounds.top - 5;
          const bWidth = textBounds.width + 10;
          const bHeight = textBounds.height + 10;

          // Draw selection border
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 2;
          ctx.setLineDash([5, 5]);
          ctx.strokeRect(left, top, bWidth, bHeight);

          // Draw resize handles
          ctx.setLineDash([]);
          ctx.fillStyle = '#ffffff';
          ctx.strokeStyle = '#3b82f6';
          ctx.lineWidth = 2;

          const handleSize = 8;
          const halfHandle = handleSize / 2;

          const drawHandle = (hx: number, hy: number) => {
            ctx.fillRect(hx - halfHandle, hy - halfHandle, handleSize, handleSize);
            ctx.strokeRect(hx - halfHandle, hy - halfHandle, handleSize, handleSize);
          };

          const right = left + bWidth;
          const bottom = top + bHeight;

          // Corner handles
          drawHandle(left, top);
          drawHandle(right, top);
          drawHandle(right, bottom);
          drawHandle(left, bottom);

          // Edge handles
          drawHandle((left + right) / 2, top);
          drawHandle((left + right) / 2, bottom);
          drawHandle(left, (top + bottom) / 2);
          drawHandle(right, (top + bottom) / 2);

          ctx.restore();
        }
      }
    }

    // Draw snap alignment guides when dragging
    if (isDragging && (snapGuides.horizontal || snapGuides.vertical)) {
      ctx.save();
      ctx.strokeStyle = '#f472b6'; // Pink color for snap guides
      ctx.lineWidth = 2;
      ctx.setLineDash([8, 4]);

      // Vertical center line (for horizontal centering)
      if (snapGuides.horizontal) {
        ctx.beginPath();
        ctx.moveTo(canvasWidth / 2, 0);
        ctx.lineTo(canvasWidth / 2, canvasHeight);
        ctx.stroke();
      }

      // Horizontal center line (for vertical centering)
      if (snapGuides.vertical) {
        ctx.beginPath();
        ctx.moveTo(0, canvasHeight / 2);
        ctx.lineTo(canvasWidth, canvasHeight / 2);
        ctx.stroke();
      }

      ctx.restore();
    }
  }, [
    canvasWidth,
    canvasHeight,
    backgroundImg,
    backgroundImageScale,
    backgroundImageOffset,
    backgroundColor,
    nftElements,
    textElements,
    selectedElementId,
    selectedElementType,
    loadedImages,
    loadedLayers,
    getNFTElementBounds,
    getTextElementBounds,
    isDragging,
    snapGuides,
  ]);

  // Map resize handle to cursor style
  const getResizeCursor = (handle: ResizeHandle): string => {
    switch (handle) {
      case 'nw':
      case 'se':
        return 'nwse-resize';
      case 'ne':
      case 'sw':
        return 'nesw-resize';
      case 'n':
      case 's':
        return 'ns-resize';
      case 'e':
      case 'w':
        return 'ew-resize';
      default:
        return 'default';
    }
  };

  // Determine cursor based on state
  const getCursor = () => {
    if (isPanning) return 'grabbing';
    if (isRotating) return 'alias';
    if (isResizing) return 'grabbing';
    if (isDragging) return 'grabbing';
    if (hoveredHandle) return getResizeCursor(hoveredHandle);
    if (backgroundImage && !selectedElementId) return 'grab';
    return 'default';
  };

  return (
    <div ref={wrapperRef} className="w-full">
      <div
        ref={containerRef}
        className="relative flex items-center justify-center rounded-2xl p-2"
      >
        <canvas
          ref={canvasRef}
          dir="ltr"
          width={canvasWidth}
          height={canvasHeight}
          className="rounded-2xl shadow-lg border-2"
          style={{
            width: viewportWidth,
            height: viewportHeight,
            cursor: getCursor(),
            touchAction: 'none',
            direction: 'ltr',
            borderColor: '#7dd3fc',
            boxShadow: '0 12px 30px rgba(15, 23, 42, 0.35)'
          }}
        />
      </div>
    </div>
  );
}
